import { useState, useEffect } from 'react';
import { Box, Typography, Grid, Card, CardContent, CardHeader, Divider, Button, Stack, Chip, Alert } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { PageHeader } from '@/components/navigation/PageHeader';
import { StatusBadge } from '@/components/dashboard/StatusBadge';
import { attendanceApi } from '@/api/attendance.api';
import { Clock, CheckCircle, XCircle, LogIn, LogOut, Timer, QrCode, Calendar, AlertTriangle, ArrowRight, History, ShieldCheck } from 'lucide-react';
import { tokens } from '@/theme/tokens';
import { useAuth } from '@/hooks/useAuth';
import { AttendanceScannerModal } from '@/components/attendance/AttendanceScannerModal';

export function MyAttendancePage() {
  const { user } = useAuth();
  const userRole = String(user?.role || '').toLowerCase();
  const isOrgAdmin = userRole === 'organization_admin' || userRole === 'super_admin';

  usePageMeta('My Attendance | Attendance', [
    { label: 'Attendance', path: '/attendance' },
    { label: 'My Attendance' },
  ]);

  const navigate = useNavigate();
  const [_loading, setLoading] = useState(false);
  const [attendance, setAttendance] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);

  useEffect(() => {
    fetchTodayAttendance();
  }, []);

  const fetchTodayAttendance = async () => {
    try {
      setLoading(true);
      const res = await attendanceApi.getMyAttendanceToday();
      if (res.success && res.attendance) {
        setAttendance(res.attendance);
      }
    } catch (error) {
      console.error('Failed to load today attendance', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '--:--';
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getBadgeStatus = (status) => {
    switch (status) {
      case 'PRESENT': return 'success';
      case 'ABSENT': return 'error';
      case 'LATE': return 'warning';
      case 'HALF_DAY': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
      <PageHeader
        title="My Attendance"
        subtitle={`Today's Attendance Status • ${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}`}
        action={
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              startIcon={<History size={16} />}
              onClick={() => navigate('/attendance/my-history')}
            >
              My Attendance History
            </Button>
            {!isOrgAdmin && (
              <Button
                variant="contained"
                startIcon={<QrCode size={16} />}
                onClick={() => setScannerOpen(true)}
              >
                Scan QR Code
              </Button>
            )}
          </Stack>
        }
      />

      {attendance?.is_late && (
        <Alert severity="warning" icon={<AlertTriangle size={20} />} sx={{ borderRadius: 2 }}>
          You checked in after the official school arrival cut-off time today.
        </Alert>
      )}

      {!attendance ? (
        <Card sx={{ width: '100%', py: 8 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <Clock size={48} color={tokens.colors.text.secondary} style={{ opacity: 0.5, marginBottom: '16px' }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Attendance Recorded Yet Today
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You have not marked your attendance for today. Please scan the QR code at check-in.
            </Typography>
            <Button
              variant="contained"
              startIcon={<QrCode size={18} />}
              onClick={() => setScannerOpen(true)}
            >
              Scan QR Now
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Top Stat Cards Row - FULL WIDTH */}
          <Grid container spacing={3} sx={{ width: '100%', m: 0 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }} sx={{ pl: '0 !important' }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600} gutterBottom>
                      Today's Status
                    </Typography>
                    <StatusBadge
                      status={getBadgeStatus(attendance.attendance_status)}
                      label={attendance.attendance_status || 'N/A'}
                    />
                  </Box>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: attendance.attendance_status === 'PRESENT' ? 'success.lighter' : 'error.lighter',
                    color: attendance.attendance_status === 'PRESENT' ? 'success.main' : 'error.main',
                    display: 'flex'
                  }}>
                    {attendance.attendance_status === 'PRESENT' ? <CheckCircle size={26} /> : <XCircle size={26} />}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600} gutterBottom>
                      Check In Time
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                      {formatTime(attendance.check_in_time)}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.lighter', color: 'primary.main', display: 'flex' }}>
                    <LogIn size={26} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600} gutterBottom>
                      Check Out Time
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                      {formatTime(attendance.check_out_time)}
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'warning.lighter', color: 'warning.main', display: 'flex' }}>
                    <LogOut size={26} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={600} gutterBottom>
                      Total Worked
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                      {attendance.worked_hours ?? 0} hrs
                    </Typography>
                  </Box>
                  <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'info.lighter', color: 'info.main', display: 'flex' }}>
                    <Timer size={26} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Details & Schedule Cards Row - PROPERLY ARRANGED */}
          <Grid container spacing={3} sx={{ width: '100%', m: 0, alignItems: 'stretch' }}>
            {/* Left Box: Detailed Timeline & Method Info */}
            <Grid size={{ xs: 12, md: 7 }} sx={{ pl: '0 !important', display: 'flex' }}>
              <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Clock size={20} />
                      <Typography variant="h6" fontWeight={600}>Today's Attendance Timeline</Typography>
                    </Box>
                  }
                  subheader="Check-in and check-out activity logs for today"
                />
                <Divider />
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 3, flex: 1, justifyContent: 'center' }}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default', border: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <LogIn size={16} color={tokens.colors.primary.main} />
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            CHECK-IN SESSION
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                          {formatTime(attendance.check_in_time)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {attendance.is_late ? 'Marked Late' : 'On Time Check-In'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'background.default', border: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <LogOut size={16} color={tokens.colors.warning.main} />
                          <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            CHECK-OUT SESSION
                          </Typography>
                        </Box>
                        <Typography variant="h6" fontWeight={700}>
                          {formatTime(attendance.check_out_time)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {attendance.check_out_time ? 'Completed Check-Out' : 'In Progress'}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Divider />

                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        ATTENDANCE METHOD
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <QrCode size={18} color={tokens.colors.text.secondary} />
                        <Typography variant="body2" fontWeight={700}>
                          {attendance.attendance_method || 'QR_STATIC'}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        ATTENDANCE DATE
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <Calendar size={18} color={tokens.colors.text.secondary} />
                        <Typography variant="body2" fontWeight={700}>
                          {attendance.attendance_date || new Date().toISOString().split('T')[0]}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        REMARKS / NOTES
                      </Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ mt: 0.5, fontStyle: attendance.remarks ? 'normal' : 'italic', color: attendance.remarks ? 'text.primary' : 'text.secondary' }}>
                        {attendance.remarks || 'No remarks added'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Right Box: Guidelines & Quick Actions */}
            <Grid size={{ xs: 12, md: 5 }} sx={{ display: 'flex' }}>
              <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <CardHeader
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <ShieldCheck size={20} />
                      <Typography variant="h6" fontWeight={600}>Attendance Guidelines</Typography>
                    </Box>
                  }
                  subheader="School policy and daily timing rules"
                />
                <Divider />
                <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, flex: 1, justifyContent: 'center' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2" fontWeight={500} color="text.secondary">Standard Working Hours</Typography>
                    <Chip label="8.0 Hours / Day" size="small" color="primary" variant="outlined" />
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2" fontWeight={500} color="text.secondary">Late Check-in Window</Typography>
                    <Typography variant="body2" fontWeight={700} color="warning.main">After 09:15 AM</Typography>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderRadius: 2, bgcolor: 'background.default' }}>
                    <Typography variant="body2" fontWeight={500} color="text.secondary">Half Day Cut-off</Typography>
                    <Typography variant="body2" fontWeight={700} color="error.main">After 11:00 AM</Typography>
                  </Box>

                  <Divider />

                  <Typography variant="caption" color="text.secondary">
                    Use the QR Code scanner from your mobile or terminal device to check in and check out each day. Your monthly attendance record can be downloaded from the history page.
                  </Typography>

                  <Button
                    variant="outlined"
                    endIcon={<ArrowRight size={16} />}
                    onClick={() => navigate('/attendance/my-history')}
                    fullWidth
                    sx={{ mt: 'auto' }}
                  >
                    View My Attendance History
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      <AttendanceScannerModal
        open={scannerOpen}
        onClose={() => {
          setScannerOpen(false);
          fetchTodayAttendance();
        }}
      />
    </Box>
  );
}
